import React, { useState, useEffect, useRef } from 'react';
import {
  Flex,
  Button,
  Box,
  Text,
  Input,
  Select,
  InputGroup,
  InputLeftElement,
  Portal,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  SimpleGrid,
} from '@chakra-ui/react';
import { X, MagnifyingGlass, CaretDown } from 'phosphor-react';
import { IKanbanCampoDefinicao, IKanbanUserOption } from '../../hooks/useKanban';
import { KanbanDatePicker } from './KanbanDatePicker';

interface KanbanFieldFiltersProps {
  campos: IKanbanCampoDefinicao[];
  filtros: Record<string, unknown>;
  usuarios?: IKanbanUserOption[];
  // eslint-disable-next-line no-unused-vars
  onChange: (filtros: Record<string, unknown>) => void;
}

// Input com debounce
const DebouncedInput: React.FC<{
  value: string;
  // eslint-disable-next-line no-unused-vars
  onDebounce: (v: string) => void;
  placeholder?: string;
  delay?: number;
}> = ({ value: externalValue, onDebounce, placeholder, delay = 500 }) => {
  const [local, setLocal] = useState(externalValue);
  const timerRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    setLocal(externalValue);
  }, [externalValue]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setLocal(v);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => onDebounce(v), delay);
  };

  return (
    <Input
      size="sm"
      h="32px"
      fontSize="12px"
      borderRadius="md"
      borderColor="gray.200"
      bg="white"
      value={local}
      onChange={handleChange}
      placeholder={placeholder}
      _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px rgba(66,153,225,0.15)' }}
      _placeholder={{ color: 'gray.300' }}
    />
  );
};

// Select de usuario com busca via Popover
const UserFilterPopover: React.FC<{
  value: string;
  // eslint-disable-next-line no-unused-vars
  onChange: (v: string) => void;
  usuarios: IKanbanUserOption[];
}> = ({ value, onChange, usuarios }) => {
  const [search, setSearch] = useState('');

  const filtered = search
    ? usuarios.filter(u => u.nome.toLowerCase().includes(search.toLowerCase()))
    : usuarios;

  const selectedUser = usuarios.find(u => String(u.id) === value);

  return (
    <Popover placement="bottom-start" isLazy>
      {({ onClose }) => (
        <>
          <PopoverTrigger>
            <Flex
              h="32px"
              px={2}
              align="center"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="md"
              bg="white"
              cursor="pointer"
              fontSize="12px"
              color={selectedUser ? 'gray.700' : 'gray.300'}
              _hover={{ borderColor: 'gray.300' }}
            >
              <Text flex={1} noOfLines={1}>
                {selectedUser ? selectedUser.nome : 'Todos'}
              </Text>
              <CaretDown size={10} color="#A0AEC0" />
            </Flex>
          </PopoverTrigger>
          <Portal>
            <PopoverContent
              w="220px"
              borderRadius="lg"
              boxShadow="lg"
              border="1px solid"
              borderColor="gray.200"
            >
              <PopoverBody p={0}>
                <Box p={1.5} borderBottom="1px solid" borderColor="gray.100">
                  <InputGroup size="xs">
                    <InputLeftElement h="28px">
                      <MagnifyingGlass size={11} color="gray" />
                    </InputLeftElement>
                    <Input
                      h="28px"
                      pl={7}
                      fontSize="12px"
                      borderRadius="md"
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      placeholder="Buscar..."
                    />
                  </InputGroup>
                </Box>
                <Box
                  maxH="180px"
                  overflowY="auto"
                  py={1}
                  css={{
                    '&::-webkit-scrollbar': { width: '3px' },
                    '&::-webkit-scrollbar-thumb': { background: '#CBD5E0', borderRadius: '2px' },
                  }}
                >
                  <Box
                    px={2.5}
                    py={1.5}
                    fontSize="12px"
                    color="gray.400"
                    cursor="pointer"
                    borderRadius="md"
                    mx={1}
                    _hover={{ bg: 'gray.50' }}
                    onClick={() => {
                      onChange('');
                      setSearch('');
                      onClose();
                    }}
                  >
                    Todos
                  </Box>
                  {filtered.map(u => (
                    <Box
                      key={u.id}
                      px={2.5}
                      py={1.5}
                      fontSize="12px"
                      color="gray.700"
                      cursor="pointer"
                      borderRadius="md"
                      mx={1}
                      bg={String(u.id) === value ? 'blue.50' : 'transparent'}
                      fontWeight={String(u.id) === value ? '600' : '400'}
                      _hover={{ bg: 'blue.50' }}
                      onClick={() => {
                        onChange(String(u.id));
                        setSearch('');
                        onClose();
                      }}
                    >
                      {u.nome}
                    </Box>
                  ))}
                  {filtered.length === 0 && (
                    <Text px={2} py={3} fontSize="11px" color="gray.300" textAlign="center">
                      Nenhum encontrado
                    </Text>
                  )}
                </Box>
              </PopoverBody>
            </PopoverContent>
          </Portal>
        </>
      )}
    </Popover>
  );
};

export const KanbanFieldFilters: React.FC<KanbanFieldFiltersProps> = ({
  campos,
  filtros,
  usuarios = [],
  onChange,
}) => {
  const hasActive = Object.values(filtros).some(v => v !== null && v !== undefined && v !== '');

  const setFilter = (key: string, value: any) => {
    const next = { ...filtros };
    if (value === null || value === undefined || value === '' || Number.isNaN(value)) {
      delete next[key];
    } else {
      next[key] = value;
    }
    onChange(next);
  };

  const sx = {
    h: '32px',
    fontSize: '12px',
    borderRadius: 'md',
    borderColor: 'gray.200',
    bg: 'white',
    _focus: { borderColor: 'blue.400', boxShadow: '0 0 0 1px rgba(66,153,225,0.15)' },
    _placeholder: { color: 'gray.300' },
  };

  // Montar lista de filtros
  const filters: { key: string; label: string; node: React.ReactNode }[] = [];

  filters.push({
    key: 'responsavel',
    label: 'Responsavel',
    node: (
      <UserFilterPopover
        value={(filtros.responsavel as string) || ''}
        onChange={v => setFilter('responsavel', v)}
        usuarios={usuarios}
      />
    ),
  });

  filters.push({
    key: 'expiraDe',
    label: 'Expira de',
    node: (
      <KanbanDatePicker
        value={(filtros.expiraDe as string) || ''}
        onChange={v => setFilter('expiraDe', v)}
        placeholder="De"
      />
    ),
  });

  filters.push({
    key: 'expiraAte',
    label: 'Expira ate',
    node: (
      <KanbanDatePicker
        value={(filtros.expiraAte as string) || ''}
        onChange={v => setFilter('expiraAte', v)}
        placeholder="Ate"
      />
    ),
  });

  campos.forEach(campo => {
    const val = filtros[String(campo.id)];
    const k = String(campo.id);

    if (campo.tipo === 'texto') {
      filters.push({
        key: k,
        label: campo.nome,
        node: (
          <DebouncedInput
            value={(val as string) || ''}
            onDebounce={v => setFilter(k, v)}
            placeholder="Filtrar..."
          />
        ),
      });
    } else if (campo.tipo === 'numero' || campo.tipo === 'moeda') {
      filters.push({
        key: k,
        label: campo.nome,
        node: (
          <DebouncedInput
            value={val !== null && val !== undefined ? String(val) : ''}
            onDebounce={v => {
              const num = parseFloat(v.replace(',', '.'));
              setFilter(k, Number.isNaN(num) ? null : num);
            }}
            placeholder={campo.tipo === 'moeda' ? 'R$ 0,00' : 'Valor'}
          />
        ),
      });
    } else if (campo.tipo === 'data') {
      filters.push({
        key: k,
        label: campo.nome,
        node: <KanbanDatePicker value={(val as string) || ''} onChange={v => setFilter(k, v)} />,
      });
    } else if (campo.tipo === 'select') {
      filters.push({
        key: k,
        label: campo.nome,
        node: (
          <Select
            {...sx}
            size="sm"
            value={(val as string) || ''}
            onChange={e => setFilter(k, e.target.value)}
            placeholder="Todos"
          >
            {(campo.opcoes || []).map(op => (
              <option key={op} value={op}>
                {op}
              </option>
            ))}
          </Select>
        ),
      });
    } else if (campo.tipo === 'checkbox') {
      filters.push({
        key: k,
        label: campo.nome,
        node: (
          <Select
            {...sx}
            size="sm"
            value={val === null || val === undefined ? '' : String(val)}
            onChange={e => {
              const v = e.target.value;
              setFilter(k, v === '' ? null : v === 'true');
            }}
            placeholder="Todos"
          >
            <option value="true">Sim</option>
            <option value="false">Nao</option>
          </Select>
        ),
      });
    }
  });

  const colCount = Math.min(filters.length, 6);

  return (
    <Box mt={3} pt={3} borderTop="1px solid" borderColor="gray.100">
      <SimpleGrid columns={{ base: 2, md: Math.min(colCount, 4), lg: colCount }} spacing={3}>
        {filters.map(f => (
          <Box key={f.key}>
            <Text
              fontSize="10px"
              color="gray.400"
              fontWeight="600"
              mb={1}
              textTransform="uppercase"
              letterSpacing="0.04em"
              noOfLines={1}
            >
              {f.label}
            </Text>
            {f.node}
          </Box>
        ))}
      </SimpleGrid>
      {hasActive && (
        <Flex mt={2} justify="flex-end">
          <Button
            size="xs"
            variant="ghost"
            leftIcon={<X size={11} weight="bold" />}
            color="red.400"
            borderRadius="md"
            fontSize="11px"
            fontWeight="500"
            _hover={{ bg: 'red.50' }}
            onClick={() => onChange({})}
          >
            Limpar filtros
          </Button>
        </Flex>
      )}
    </Box>
  );
};
